import React, { useState, useEffect } from "react";
import { Pie, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
} from "chart.js";
import ShowModal from "../utils/ShowModal";
import ExpenseCard from "./ExpenseCard";
import CustomLoading from "./customLoading/CustomLoading";
import { getExpenseDetails, getExpenseAnalytics } from "../services/expense";
import { toast } from "react-toastify";
import DateRangePicker from "./inputFields/DateRangePicker";

// Register Chart.js components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
);

const ExpenseDetailedView = ({ expense, isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Income, 2: Expense
    const [expenses, setExpenses] = useState([]);
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFarm, setSelectedFarm] = useState("All");
    const [farms, setFarms] = useState([]);
    const [isFilterApplied, setIsFilterApplied] = useState(false);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
        end: new Date()
    });

    // Chart data states
    const [donutData, setDonutData] = useState({
        income: 0,
        expense: 0
    });
    const [incomeChartData, setIncomeChartData] = useState([]);
    const [expenseChartData, setExpenseChartData] = useState([]);
    const [loadingCharts, setLoadingCharts] = useState(true);
    const defaultStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    useEffect(() => {
        if (isOpen && expense) {
            fetchExpenseDetails();
            fetchAnalyticsData();
        }
    }, [isOpen, expense]);

    useEffect(() => {
        filterExpenses();
    }, [expenses, activeTab, selectedFarm, isFilterApplied]);

    const fetchExpenseDetails = async () => {
        setLoading(true);
        try {
            // Build query string for filters
            const queryParams = new URLSearchParams();
            if (isFilterApplied) {
                if (selectedFarm !== "All") {
                    queryParams.append("farm", selectedFarm);
                }
                if (dateRange.start) {
                    queryParams.append("start_date", dateRange.start.toISOString().split('T')[0]);
                }
                if (dateRange.end) {
                    queryParams.append("end_date", dateRange.end.toISOString().split('T')[0]);
                }
            }

            const response = await getExpenseDetails(expense?.id);

            if (response.status === 200) {
                const data = response.data;
                setExpenses(data.data || []);

                // Extract farms from expenses
                const allFarms = [];
                (data.data || []).forEach(exp => {
                    if (exp.farm) {
                        exp.farm.forEach(farm => {
                            if (!allFarms.find(f => f.id === farm.id)) {
                                allFarms.push(farm);
                            }
                        });
                    }
                });
                setFarms(allFarms);
            } else {
                toast.error("Failed to fetch expense details");
            }
        } catch (error) {
            console.error("Error fetching expense details:", error);
            toast.error("Error loading expense details");
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalyticsData = async () => {
        setLoadingCharts(true);
        try {
            const response = await getExpenseAnalytics(expense?.id);

            if (response.status === 200) {
                const data = response.data;
                setDonutData({
                    income: data.total_income || 0,
                    expense: data.total_expense || 0,
                    incomeFormatted: data.total_earning_updated || "0",
                    expenseFormatted: data.total_expense_updated || "0"
                });

                setIncomeChartData(data.top_categories_income || []);
                setExpenseChartData(data.top_categories_expense || []);
            } else {
                toast.error("Failed to fetch analytics data");
            }
        } catch (error) {
            console.error("Error fetching analytics:", error);
            toast.error("Error loading analytics data");
        } finally {
            setLoadingCharts(false);
        }
    };

    const filterExpenses = () => {
        let filtered = expenses;

        // Filter by type
        if (activeTab === 1) {
            filtered = filtered.filter(exp => exp.type === "Income");
        } else if (activeTab === 2) {
            filtered = filtered.filter(exp => exp.type === "Expense");
        }

        // Filter by farm
        if (selectedFarm !== "All") {
            filtered = filtered.filter(exp =>
                exp.farm && exp.farm.some(farm => farm.id.toString() === selectedFarm)
            );
        }

        setFilteredExpenses(filtered);
    };

    const handleFarmFilter = (farmId) => {
        setSelectedFarm(farmId);
        setIsFilterApplied(farmId !== "All");
        // Refetch data with new farm filter
        fetchExpenseDetails();
    };

    const handleDateRangeChange = (start, end) => {
        setDateRange({ start, end });
        setIsFilterApplied(true);
        // Refetch data with new date range
        fetchExpenseDetails();
    };

    const resetFilters = () => {
        setSelectedFarm("All");
        setDateRange({ start: defaultStartDate, end: new Date() });
        setIsFilterApplied(false);
        fetchExpenseDetails();
    };

    const getDonutChartData = () => ({
        labels: ['Income', 'Expense'],
        datasets: [
            {
                data: [donutData.income, donutData.expense],
                backgroundColor: ['#10B981', '#EF4444'],
                borderWidth: 0,
            },
        ],
    });

    const getBarChartData = (data, color) => ({
        labels: data.map(item => item.category),
        datasets: [
            {
                label: 'Amount (₹)',
                data: data.map(item => item.total),
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1,
                borderRadius: 6,
                borderSkipped: false,
            },
        ],
    });

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function (context) {
                        return `₹${context.parsed.toLocaleString()}`;
                    }
                }
            }
        },
        cutout: '65%',
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function (context) {
                        return `₹${context.parsed.x.toLocaleString()}`;
                    }
                }
            }
        },
        elements: {
            bar: {
                barThickness: 14,
                maxBarThickness: 18,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false,
                },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 10,
                    }
                }
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 10,
                    },
                    callback: function (value) {
                        return '₹' + value.toLocaleString();
                    }
                }
            }
        },
    };

    const tabs = ["All", "Income", "Expense"];
    const tabCounts = {
        all: expenses.length,
        income: expenses.filter((e) => e.type === "Income").length,
        expense: expenses.filter((e) => e.type === "Expense").length,
    };

    return (
        <ShowModal
            isOpen={isOpen}
            onRequestClose={onClose}
            title={`Expense Details - ${expense?.commodity_name || "Unknown"}`}
            isSidebar={true}
            modalWidth="50%"
        >
            <div className="h-full flex flex-col">
                {/* Header Info */}
                <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl overflow-hidden flex items-center justify-center border-2 border-white shadow-sm">
                            {expense?.commodity_image ? (
                                <img
                                    src={expense.commodity_image}
                                    alt={expense.commodity_name || expense.commodity}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="text-2xl">🌾</span>
                            )}
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">
                                {expense?.commodity_name || expense?.commodity || "Expense Details"}
                            </h2>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                    🗓️ {expense?.season || "All Seasons"}
                                </span>
                                {expense?.percentage && (
                                    <span className="flex items-center gap-1">
                                        📈 {expense.percentage}% growth
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-3">
                    {tabs.map((tab, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveTab(index)}
                            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${activeTab === index
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                        >
                            {tab}
                            {index === 0 && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{tabCounts.all}</span>
                            )}
                            {index === 1 && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">{tabCounts.income}</span>
                            )}
                            {index === 2 && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{tabCounts.expense}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Charts Section */}
                <div className="mb-4">
                    {loadingCharts ? (
                        <div className="h-40 flex items-center justify-center">
                            <CustomLoading />
                        </div>
                    ) : (
                        <div className="h-40">
                            {activeTab === 0 && (
                                <div className="h-full grid grid-cols-2 gap-3">
                                    <div className="h-full">
                                        <Pie
                                            data={getDonutChartData()}
                                            options={chartOptions}
                                        />
                                    </div>
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="flex justify-center gap-6">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm"></div>
                                                <div className="text-center">
                                                    <div className="text-sm font-bold text-green-600">
                                                        ₹{donutData.incomeFormatted || donutData.income.toLocaleString()}
                                                    </div>
                                                    <div className="text-[11px] text-gray-500">Income</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-red-500 rounded-full shadow-sm"></div>
                                                <div className="text-center">
                                                    <div className="text-sm font-bold text-red-600">
                                                        ₹{donutData.expenseFormatted || donutData.expense.toLocaleString()}
                                                    </div>
                                                    <div className="text-[11px] text-gray-500">Expense</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 w-full p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                                            <div className="text-center">
                                                <div className="text-[11px] text-gray-600 mb-1">Net Result</div>
                                                <div className={`text-lg font-bold ${donutData.income >= donutData.expense
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                                    }`}>
                                                    {donutData.income >= donutData.expense ? '+' : '-'}₹{Math.abs(donutData.income - donutData.expense).toLocaleString()}
                                                </div>
                                                <div className="text-[11px] text-gray-500">
                                                    {donutData.income >= donutData.expense ? 'Profit' : 'Loss'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 1 && (
                                <div className="h-full">
                                    <Bar
                                        data={getBarChartData(incomeChartData, '#10B981')}
                                        options={barChartOptions}
                                    />
                                </div>
                            )}
                            {activeTab === 2 && (
                                <div className="h-full">
                                    <Bar
                                        data={getBarChartData(expenseChartData, '#EF4444')}
                                        options={barChartOptions}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Filters */}
                <div className="mb-3 sticky top-0 z-10 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                    <div className="flex items-center justify-between py-2">
                        <h3 className="text-sm font-medium text-gray-700">Transactions <span className="text-xs text-gray-500">({filteredExpenses.length})</span></h3>
                        <div className="flex items-center gap-2">
                            <DateRangePicker
                                compact
                                value={{
                                    start: dateRange.start ? dateRange.start.toISOString().split('T')[0] : '',
                                    end: dateRange.end ? dateRange.end.toISOString().split('T')[0] : ''
                                }}
                                onChange={({ start, end }) => {
                                    const newStart = start ? new Date(start) : null;
                                    const newEnd = end ? new Date(end) : null;
                                    handleDateRangeChange(newStart, newEnd);
                                }}
                                placeholder="Choose date range"
                            />
                            <button
                                onClick={resetFilters}
                                className="text-[11px] px-2 py-1 border rounded text-gray-600 hover:bg-gray-50"
                                title="Reset filters"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    {/* Farm Filter */}
                    <div className="flex flex-wrap gap-2 pb-2">
                        <button
                            onClick={() => handleFarmFilter("All")}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectedFarm === "All"
                                ? "bg-blue-500 text-white border-blue-500"
                                : "bg-white text-blue-500 border-blue-500 hover:bg-blue-50"
                                }`}
                        >
                            All
                        </button>
                        {farms.map((farm) => (
                            <button
                                key={farm.id}
                                onClick={() => handleFarmFilter(farm.id.toString())}
                                className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectedFarm === farm.id.toString()
                                    ? "bg-blue-500 text-white border-blue-500"
                                    : "bg-white text-blue-500 border-blue-500 hover:bg-blue-50"
                                    }`}
                            >
                                {farm.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Transactions List */}
                <div>
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <CustomLoading />
                        </div>
                    ) : filteredExpenses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {filteredExpenses
                                .slice()
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .map((exp) => (
                                    <ExpenseCard
                                        key={exp.id}
                                        expense={exp}
                                        onClick={() => {
                                            // Handle individual expense click if needed
                                            console.log("Expense clicked:", exp);
                                        }}
                                    />
                                ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                            <div className="text-4xl mb-2">📊</div>
                            <p className="text-sm">No transactions found</p>
                        </div>
                    )}
                </div>
            </div>
        </ShowModal>
    );
};

export default ExpenseDetailedView;
